import { AxiosResponse } from 'axios';
import { HttpMethod } from '../utils/enums/http-method.enum';
import { RequestClientInterfaceService } from './request-client.interface.service';
import {
    COGNITO_TOKEN_NAME,
    DEALER_PORTAL_URL,
    PERMISSIONS_TOKEN_NAME,
} from '../config';
import {
    FundingSearchStatusOptions,
    FundingSearchTypes,
    FundingStatusTableData,
    IEFTNotifications,
    IFundingStatusObject,
} from './models/funding.service';

const NO_DATE = 0;

/**
 * Class responsible for manage the API calls and data request of the App
 */
export class FundingService {
    private readonly dealerPortalUrlStructure = `${DEALER_PORTAL_URL}`;
    private client: RequestClientInterfaceService;
    private headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: '',
    };
    /**
     * Instantiates a new ClientService
     */
    constructor() {
        this.client = new RequestClientInterfaceService();
    }

    /**
     * Retrieves the funding widget data
     * @param dealerId DealerId for funding widget
     */
    public async fundingStatusWidget(dealerId: string): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/fundingWidget`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { dealerId, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    public async fundingDetails(
        dealerId: string,
        accountNumber: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/fundingDetail`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                accountNumber,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    public async fundingHistory(dealerId: string, accountNumber: string) {
        const url = `${this.dealerPortalUrlStructure}/fundingHistory`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                accountNumber,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    public async fundingStatusSearch(
        dealerId: string,
        searchType: FundingSearchTypes,
        searchQuery: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/fundingSearch`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                searchType,
                searchValue: searchQuery,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    public async fundingEFT(
        dealerId: string,
        ccNumber: string,
        activityDateStart: string,
        activityDateEnd: string,
    ): Promise<AxiosResponse<IEFTNotifications[]>> {
        const url = `${this.dealerPortalUrlStructure}/fundingEftNotifications`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                ccNumber,
                activityDateStart,
                activityDateEnd,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }
    public mapResponse(data: IFundingStatusObject[]): FundingStatusTableData[] {
        return data
            .map((item) => {
                return {
                    key: item.Key,
                    name: item.BusinessName.length
                        ? item.BusinessName
                        : `${item.PersonName.FirstName} ${item.PersonName.LastName}`,
                    accountNumber: item.ContractId,
                    status: item.Status as FundingSearchStatusOptions,
                    collateral: `${item.Vehicle.Year} ${item.Vehicle.Make} ${item.Vehicle.Model}`,
                    vin: item.Vehicle.VIN.Number,
                    dateReceived: item.StatusDate,
                };
            })
            .sort(
                (a, b) =>
                    (Date.parse(b.dateReceived) || NO_DATE) -
                    (Date.parse(a.dateReceived) || NO_DATE),
            );
    }

    public abort() {
        this.client.controller.abort();
    }
}
